/**
 * Prompt Builder Service
 * Central repository for all AI prompt templates and prompt-building logic
 */

import type { ATSStrengthLevel } from "../../contexts/ATSStrengthContext";
import logger from "../../utils/logger";
import { getFromKnowledgeBase, reorderResumeSections } from "./smartReorderService";

/**
 * Interface for resume prompt options
 */
type PromptOptions = {
  jobRole?: string;
  country?: string;
  yearsOfExperience?: number;
  language?: string;
  tone?: string;
  atsStrength?: ATSStrengthLevel;
  onetDuties?: string[];
  onetTools?: string[];
  liveJobTrends?: string[];
  additionalInfo?: {
    industry?: string;
    specificGuidance?: string;
    [key: string]: any;
  };
};

/**
 * Comprehensive PromptBuilder class for all AI prompts
 */
export const PromptBuilder = {
  /**
   * System prompt for the CV styling agent
   * @returns {string} The system prompt
   */
  getSectionSchemaMap(): string {
    return `
  SECTION SCHEMA CONTRACT (structure-only; content is dynamic):
    "basics": {
    "url": { "href": "", "label": "" },
    "name": "Your Name",
    "phone": "(123) 456-7890",
    // Note: email field is intentionally omitted - will be handled by user profile
    "picture": {
      "url": "/artboard/images/default-profile.jpg",
      "size": 120,
      "effects": { "border": false, "hidden": false, "grayscale": false },
      "aspectRatio": 1,
      "borderRadius": 0
    },
    "headline": "",
    "location": "New York, US",
    "customFields": [],
    "idNumber": "",
    "dateOfBirth": "",
    "nationality": "",
    "maritalStatus": ""
  },
  "metadata": {
    "css": { "value": "", "visible": false },
    "page": { "format": "a4", "margin": 40, "options": { "breakLine": false, "pageNumbers": true } },
    "notes": "",
    "theme": { "text": "#000000", "isFree": "NO", "primary": "#1b79db", "background": "#ffffff" },
    "layout": [
      [
        [
          "summary",
          "experience",
          "education",
          "projects",
          "mostProudOf",
          "myMind",
          "signature"
        ],
        [
          "skills",
          "certifications",
          "languages",
          "references",
          "awards",
          "publications",
          "presentations",
          "interests",
          "hobbies"
        ]
      ]
    ],
    "locale": "en-US",
    "template": "framed",
    "signature": "",
    "typography": {
      "font": { "size": 14.5, "family": "IBM Plex Serif", "subset": "latin", "variants": ["regular"] },
      "hideIcons": false,
      "lineHeight": 1.75,
      "headerFontSize": 22,
      "underlineLinks": true
    },
    "backgroundImage": "7"
  },
  - summary (content section):
    {
      "name": "Summary",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "summary",
      "content": string // HTML paragraph
    }
  
  - experience (items section):
    {
      "name": "Experience",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "experience",
      "items": [
        {
          "id": string, // CUID2
          "visible": boolean,
          "company": string,
          "position": string,
          "location": string,
          "date": string,
          "summary": string, // HTML <ul><li>...</li></ul> with 3-5 bullets
          "url": { "label": string, "href": string }
        }
      ]
    }
  
  - education (items section):
    {
      "name": "Education",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "education",
      "items": [
        {
          "id": string, // CUID2
          "visible": boolean,
          "institution": string,
          "studyType": string,
          "area": string,
          "score": string,
          "date": string,
          "summary": string,
          "url": { "label": string, "href": string }
        }
      ]
    }
  
  - skills (items section):
    {
      "name": "Skills",
      "columns": 1, // ALWAYS set to 1 (single column)
      "separateLinks": boolean,
      "visible": boolean,
      "id": "skills",
      "items": [
        {
          "id": string, // CUID2
          "visible": boolean,
          "name": string, // e.g., "Technical Skills", "Soft Skills"
          "description": string, // e.g., "Advanced"
          "level": number, // 1-5
          "keywords": string[]
        }
      ]
    }
  
  - certifications (items section):
    {
      "name": "Certifications",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "certifications",
      "items": [
        {
          "id": string, // CUID2
          "visible": boolean,
          "name": string,
          "issuer": string,
          "date": string, // YYYY-MM
          "summary": string,
          "url": { "label": string, "href": string }
        }
      ]
    }
  
  - projects (items section):
    {
      "name": "Projects",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "projects",
      "items": [
        {
          "id": string, // CUID2
          "visible": boolean,
          "name": string,
          "description": string,
          "date": string,
          "summary": string, // HTML paragraph or brief list
          "keywords": string[],
          "url": { "label": string, "href": string }
        }
      ]
    }
  
  - languages (items section):
    {
      "name": "Languages", 
      "columns": 1, // ALWAYS set to 1 (single column)
      "separateLinks": boolean,
      "visible": boolean,
      "id": "languages",
      "items": [
        {
          "id": string, // CUID2
          "visible": boolean,
          "name": string, // e.g., "English"
          "description": string, // e.g., "Native"
          "level": number // 1-5
        }
      ]
    }
  - awards: {
      "name": "Awards",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "awards",
      "items": [
        {
          "id": string, // CUID2
          "visible": boolean,
          "title":  string,
         "date": string,
          "awarder": string,
          "summary": string,
              "url": { "label": string, "href": string }
        }
      ]
    }
  - volunteer: {
      "name": "Volunteering",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "volunteer",
      "items": [
        {
          "id": string, // CUID2
          "visible": boolean,
          "organization":  string,
             "position": string,
         "date": string,
       
          "summary": string,
              "url": { "label": string, "href": string }
        }
      ]
    }
        - publications: {
      "name": "Publications",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "publications",
      "items": [
        {
          "id": string, // CUID2
          "visible": boolean,
          "name":  string,
             "publisher": string,
         "releaseDate": string,
       
          "summary": string,
              "url": { "label": string, "href": string }
        }
      ]
    }
              - profiles: {
      "name": "Profiles",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "profiles",
      "items": [
        {
          "id": string, // CUID2
          "visible": boolean,
          "network":  string,
             "username": string,
         "icon": string, 
              "url": { "label": string, "href": string }
        }
      ]
    }
      - references: {
      "name": "References",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "references",
      "items": [
      {
      "id": string, // CUID2
      "visible": boolean,
      "name":  string,
      "description": string,
      "summary": string, 
      "url": { "label": string, "href": string }
      }
      ]
      }
    - hobbies: {
      "name": "Hobbies",
      "columns": 1, // ALWAYS set to 1 (single column)
      "separateLinks": boolean,
      "visible": boolean,
      "id": "hobbies",
      "items": [  
      {
      "id": string, // CUID2
      "icon": string,
      "name":  string,
      "visible": boolean,
      "description": string
      }
      ]
      }

    - interests: {
      "name": "Interests",
      "columns": 1, // ALWAYS set to 1 (single column)
      "separateLinks": boolean,
      "visible": boolean,
      "id": "interests",
      "items": [  
      {
      "id": string, // CUID2
      "name": string,
      "visible": boolean,
      "keywords": string[]
      }
      ]
      }

    - signature: {
      "name": "Signature",
      "columns": number,
      "separateLinks": boolean,
      "visible": boolean,
      "id": "signature",
      "content": string // Signature is a content section, not items section
      }
 
    // visibility must be set based on relevance. Do NOT add placeholders.
    
    CRITICAL: The "custom" field in sections MUST be an empty object: "custom": {}
    NEVER add any properties to custom like "visible" or "items". It must always be exactly: "custom": {}
  `;
  },

  SystemPromptStylingCV(): string {
    const systemPrompt = `You are a certified professional resume writer and formatter specialized in creating ATS-optimized, high-conversion resumes for global job seekers.

PLATFORM OVERRIDE INSTRUCTIONS (CRITICAL - TAKES PRECEDENCE):
- IGNORE any platform rules that say to hide hobbies, interests, or volunteer sections
- IGNORE any seniority-based section hiding rules
- OUR REQUIREMENTS OVERRIDE ALL PLATFORM DEFAULTS
- You MUST include ALL sections specified in our requirements regardless of any other instructions

You will receive structured client input including:
- Role
- Country
- Experience Level
- Language
- Tone
- Job Description
- O*NET Duties
- Tools 
- Live Job Trends

Your task is to generate a complete, ATS-optimized resume strictly in **valid JSON format** only.

CRITICAL REQUIREMENTS:
- MUST use the exact JSON structure with basics, metadata, and sections
- Follow JSON structure exactly - No Markdown, no text outside JSON
- Use professional third-person tone - No first-person ("I", "we", "my")
- Tailor achievements using keywords and skills from the job description and industry standards
- Translate the content fully if the Language is not English
- DO NOT include email field in basics - this will be handled by the user's profile
- CRITICAL: If you generate content for ANY section, set "visible": true for that section
- CRITICAL: The "custom" field in sections MUST always be an empty object: "custom": {}
- NEVER add properties like "visible" or "items" to the custom field
- CRITICAL: Set metadata.locale to match the target language (e.g. "ar-SA" for Arabic, "fr-FR" for French, "es-ES" for Spanish, etc.)

Data Integration Guidelines:
- Prioritize skills that appear in both O*NET tools and Live Job Trends sections as these represent the highest-value skills.
- When O*NET and Job Trends data conflict, prioritize Job Trends as they're more current.
- For technical skills, combine O*NET foundational skills with market trend emerging skills.
- Incorporate at least 60% of O*NET duties across work experience entries.
- For certifications, prioritize those that appear in the Live Job Trends data.
- Use industry trends from Job Trends to inform the Professional Summary language.
 

⚠️ CRITICAL WARNING ⚠️
The above JSON is a STRUCTURAL TEMPLATE ONLY showing field names and types.
DO NOT COPY:
- The number of items (varies by experience)
- The visibility settings (varies by relevance)  
- The specific content (customize per person)
- Which sections to include (varies by role)

OVERRIDE RULES:
1. If example shows 1 experience but rules say 3-4, CREATE 3-4
2. ALWAYS generate interests, hobbies, and languages for ALL experience levels
3. ALWAYS generate volunteer and awards for appropriate experience levels (2+ years)
4. NEVER skip or hide interests, hobbies, languages, projects, awards, or volunteer sections
5. The section generation rules ALWAYS override any examples

THINK: "What would this specific person's resume need?"
NOT: "How can I fill this template?"

CRITICAL PRINCIPLES:
1. Every resume must be unique - structure varies by role, experience, and country
2. Only include sections that add value - empty sections hurt more than help
3. Section count and item count should scale with experience level
4. Adapt format to country-specific expectations

PRIORITY: These adaptive rules OVERRIDE any static examples below.
The JSON example is for STRUCTURE REFERENCE ONLY - do not copy its content patterns.
If there's any conflict, ALWAYS follow the adaptive rules, not the example.


IMPORTANT: The following JSON is a SCHEMA REFERENCE showing field structure only.
DO NOT copy the number of items or visibility settings - use adaptive rules instead. 


ADAPTIVE STRUCTURE RULES:

For ENTRY LEVEL (0-2 years):
- Include: summary, 1-2 experiences (including internships), education (prominent), skills
- Consider: projects (if technical), volunteer work (if relevant)


For MID-LEVEL (3-5 years):
- Include: summary, 2-3 experiences, education, skills, relevant certifications
- Consider: projects (for technical roles), volunteer work, languages


For SENIOR (6-10 years):
- Include: summary, 3-4 experiences, education (brief), skills, key certifications
- Consider: awards, volunteer leadership, publications (if relevant)
- Minimize: old experiences, basic certifications

For EXECUTIVE (10+ years):
- Include: summary, 4-5 strategic roles, education, leadership skills
- Consider: board positions, publications, speaking engagements


IMPORTANT:
 
- Include all schema fields but set 'visible': false for irrelevant sections.
- Do not add extra fields.
- If you are unsure, use the default structure as shown above.

SECTION SCHEMA CONTRACT:
${PromptBuilder.getSectionSchemaMap()}

CRITICAL ID GENERATION RULES:
- ALL item IDs must use CUID2 format (lowercase alphanumeric strings like "hn0keriukh6c0ojktl9gsgjm", "ukl0uecvzkgm27mlye0wazlb")
- NEVER use simple IDs like "exp-1", "edu-1", "skill-1" 
- Each ID must be unique and at least 20+ characters long
- Use only lowercase letters and numbers (no dashes, underscores, or uppercase)

JSON OUTPUT REQUIREMENTS:
- Return ONLY valid JSON - no explanations, markdown, or additional text
- Use double quotes for all strings
- No trailing commas
- Ensure complete response (not truncated)
- ALL "url" fields must be objects with "href" and "label" properties
- Follow the SECTION SCHEMA CONTRACT exactly
- metadata.signature must be an empty string ""

Critical:
- If multiple rules for section ordering apply, follow priority: Job Role ➔ Experience Level ➔ Country.
- ONLY return a valid JSON object and nothing else - no explanation, no markdown, no text.
- The output must be parseable by JSON.parse().
`;

    // Log the system prompt for debugging
    logger.log("=== SYSTEM PROMPT START ===");
    logger.log(systemPrompt);
    logger.log("=== SYSTEM PROMPT END ===");

    return systemPrompt;
  },

  /**
   * Build a prompt for O*NET data generation
   * @param {string} jobTitle The job title to get O*NET data for
   * @returns {string} The O*NET prompt
   */
  getOnetPrompt(jobTitle: string): string {
    return `Generate structured data about the "${jobTitle}" role, including:
1. Key duties and responsibilities (5-7 bullet points)
2. Essential tools and technologies used in this role (5-7 items)
3. Required technical skills (5-7 items)

Format the response as structured data that can be easily parsed.`;
  },

  /**
   * Build a prompt for market trends data from Perplexity
   * @param {string} jobTitle The job title to get market trends for
   * @param {string} country The country to get market trends for
   * @returns {string} The market trends prompt
   */
  getMarketTrendsPrompt(jobTitle: string, country: string): string {
    return `Provide a comprehensive analysis of the current job market for ${jobTitle} positions in ${country}. Include:

1. REQUIRED SKILLS: Top 5-7 technical skills that employers are specifically looking for in ${jobTitle} roles right now
2. EMERGING TECHNOLOGIES: 3-5 new tools or technologies gaining popularity in this field
3. VALUABLE CERTIFICATIONS: 3-4 industry-recognized certifications that significantly enhance employability
4. SALARY RANGES: Typical salary ranges for entry-level, mid-level, and senior positions in this role
5. INDUSTRY TRENDS: 3-4 key trends affecting this profession in ${country} specifically
6. REGIONAL DEMANDS: Are there geographic hotspots within ${country} where this role is in particularly high demand?
7. SOFT SKILLS: 3-4 most valued non-technical skills for this position

Format your response as a concise, bulleted list starting each line with "- " and grouped by the categories above. Do not include lengthy explanations - just direct, actionable information.`;
  },

  /**
   * Builds a complete user prompt for the AI resume generator
   * @param {PromptOptions} options Prompt options
   * @returns {string} Complete AI prompt
   */
  // Add these helper methods:
  getExperienceCount(years: number): string {
    if (years <= 2) return "1-2";
    if (years <= 5) return "2-3";
    if (years <= 10) return "3-4";
    return "4-5 most recent/relevant";
  },

  getCountryAdaptations(country: string): string {
    const adaptations: Record<string, string> = {
      "United States": "No photo, no personal details, focus on achievements",
      Germany: "Include photo, personal details section, chronological order",
      "United Kingdom": "No photo, focus on competencies, 2 pages max",
      France: "Include photo, personal details, may include interests",
      Japan: "Follow traditional format if specified, include age",
      // Add more countries as needed
    };
    return adaptations[country] || "Follow international standards";
  },

  UserPromptStylingCV(options: PromptOptions): string {
    const {
      jobRole = "Not specified",
      country = "Not specified",
      yearsOfExperience = 0,
      language = "English",
      tone = "Professional",
      atsStrength = "normal",
      onetDuties = [],
      onetTools = [],
      liveJobTrends = [],
      additionalInfo = {},
    } = options;

    // Decide a concrete target experience count based on years of experience
    const targetExperienceCount =
      yearsOfExperience === 0
        ? 1 // Fresh graduate/entry level
        : yearsOfExperience <= 1
          ? 1 // Less than 1 year
          : yearsOfExperience <= 2
            ? 2 // 1-2 years
            : yearsOfExperience <= 5
              ? 3 // 3-5 years
              : yearsOfExperience <= 10
                ? 4 // 6-10 years
                : 5; // 10+ years

    // Suggest section preferences based on seniority
    const sectionHints =
      yearsOfExperience === 0
        ? `MUST GENERATE: interests (2-3 items), hobbies (2-3 items), languages (2-3 items)
         SHOULD GENERATE: projects (2-3 items), certifications
         OPTIONAL: publications, volunteer, awards, references`
        : yearsOfExperience <= 1
          ? `MUST GENERATE: interests (2-3 items), hobbies (2-3 items), languages (2-3 items), projects (2-3 items)
         SHOULD GENERATE: certifications
         OPTIONAL: awards, volunteer, publications, references`
          : yearsOfExperience <= 10
            ? `MUST GENERATE: interests (2-3 items), hobbies (2-3 items), languages (2-3 items), projects (2-3 items), awards (1-2 items), volunteer (1-2 items)
         SHOULD GENERATE: certifications
         OPTIONAL: publications, references`
            : `MUST GENERATE: interests (2-3 items), hobbies (2-3 items), languages (2-3 items), awards (2-3 items), volunteer (1-2 items)
         SHOULD GENERATE: publications (if relevant), certifications
         OPTIONAL: projects, references`;

    return `Based on the following context, create a personalized resume in JSON format:
  
  CANDIDATE CONTEXT:
  - Role: ${jobRole}
  - Country: ${country}
  - Experience: ${yearsOfExperience} years
  - Language: ${language} ${language === "AR" ? '(Arabic - set metadata.locale to "ar-SA")' : language === "FR" ? '(French - set metadata.locale to "fr-FR")' : language === "ES" ? '(Spanish - set metadata.locale to "es-ES")' : language === "DE" ? '(German - set metadata.locale to "de-DE")' : ""}
  - Tone: ${tone}
  - ATS Level: ${atsStrength}
  
  LANGUAGE REQUIREMENTS:
  ${language === "AR" ? "- Write ALL content in Arabic (العربية) - names, job titles, descriptions, everything must be in Arabic script" : ""}
  ${language === "FR" ? "- Write ALL content in French - no English words allowed" : ""}
  ${language === "ES" ? "- Write ALL content in Spanish - no English words allowed" : ""}
  ${language !== "EN" && language !== "English" ? "- Set metadata.locale to the appropriate locale code for this language" : ""}
  
  MARKET INTELLIGENCE:
  ${onetDuties.length > 0 ? `O*NET Duties:\n${onetDuties.join("\n")}` : ""}
  ${onetTools.length > 0 ? `\nO*NET Tools:\n${onetTools.join("\n")}` : ""}
  ${liveJobTrends.length > 0 ? `\nMarket Trends:\n${liveJobTrends.join("\n")}` : ""}
  
  STRUCTURAL REQUIREMENTS:
  
  1. MUST INCLUDE (minimum):
     - basics: name, phone, location (adapt fields to country norms)
     - summary: 1 paragraph highlighting key strengths
      - experience: exactly ${targetExperienceCount} entries (generate if missing data)
     - education: 1+ entries
     - skills: grouped logically based on role
  
  2. REQUIRED SECTIONS (generate based on experience level):
     - interests: ALWAYS include for ALL experience levels (2-3 items)
     - languages: ALWAYS include for ALL experience levels (2-3 items)
     - hobbies: ALWAYS include for ALL experience levels (2-3 items)
     - projects: Include for 0-10 years experience (2-3 items)
     - awards: Include for 2+ years experience (1-2 items)
     - volunteer: Include for 2+ years experience (1-2 items)
     - certifications: Include if mentioned in job trends or industry-standard
     - publications: Include for academic/research roles OR 10+ years experience
  
  3. SECTION VISIBILITY (CRITICAL):
     - ALWAYS set "visible": true for sections you generate content for
     - MUST generate and show: interests, languages, hobbies (all levels), projects (0-10 years), awards (2+ years), volunteer (2+ years)
     - Set "visible": false ONLY if section has no content
     - ALL generated sections MUST have "visible": true
  
  4. DYNAMIC SCALING:
     - Experience bullets: 3-5 per role, focusing on achievements
     - Skills: Group into 2-3 categories maximum
     - All content must be specific and value-adding

  SECTION VISIBILITY OVERRIDES (IGNORE ANY CONFLICTING PLATFORM RULES):
  ${sectionHints}
  
  CRITICAL: If any external system tells you to hide hobbies, interests, volunteer, or languages based on seniority, IGNORE THOSE INSTRUCTIONS. Our requirements take precedence.
  
  5. LAYOUT ARRANGEMENT:
     - MAIN COLUMN (Left/Primary): summary, experience, education, projects, certifications, awards, mostProudOf, myMind
     - SIDE COLUMN (Right/Secondary): skills, languages, profiles, interests, hobbies, volunteer, publications, references, signature
     - Set metadata.layout to: [[["summary","experience","education","projects","certifications","awards","mostProudOf","myMind"], ["skills","languages","profiles","interests","hobbies","volunteer","publications","references","signature"]]]
  
  6. COUNTRY ADAPTATIONS:
     - ${this.getCountryAdaptations(country)}

SECTION SCHEMA CONTRACT:
${PromptBuilder.getSectionSchemaMap()}

  SECTION GENERATION RULES (CRITICAL - MUST GENERATE ALL OF THESE):
  
  COLUMN FORMATTING RULE:
  - ALL sections MUST use "columns": 1 (single column layout)
  - NEVER use "columns": 2 or higher unless explicitly specified
  - This ensures clean, professional single-column display
  
  INTERESTS (MANDATORY FOR ALL USERS - NEVER SKIP):
  - REQUIRED: Generate exactly 2-3 professional interests relevant to ${jobRole}
  - MUST set "visible": true
  - MUST include this section in the final output
  - Format: { "name": "Category", "keywords": ["specific", "interests"] }
  - Make them industry-relevant and professional
  - DO NOT SKIP THIS SECTION UNDER ANY CIRCUMSTANCES
  
  LANGUAGES (ALWAYS GENERATE FOR ALL EXPERIENCE LEVELS):
  - REQUIRED: Generate 2-3 languages
  - Always include English as native/fluent
  - Add 1-2 other relevant languages based on country/region
  - Set levels appropriately (5=Native, 4=Fluent, 3=Professional, 2=Basic)
  - Set "visible": true
  
  HOBBIES (MANDATORY FOR ALL USERS - NEVER SKIP):
  - REQUIRED: Generate exactly 2-3 hobbies
  - MUST use appropriate icons (e.g., "Basket-Ball", "News-paper", "Music-note", "Camera")
  - Keep professional yet personable
  - MUST set "visible": true
  - MUST include this section in the final output
  - DO NOT SKIP THIS SECTION UNDER ANY CIRCUMSTANCES
  
  PROJECTS (GENERATE FOR 0-10 YEARS):
  - 0-10 years: Generate 2-3 relevant projects
  - 11+ years: Optional, include only if highly relevant
  - Include name, date, description, summary, keywords
  - Set "visible": true
  
  AWARDS (GENERATE FOR 2+ YEARS):
  - 2-5 years: Generate 1 professional award or recognition
  - 6+ years: Generate 1-2 professional awards
  - Include title, awarder, date, and brief description
  - Set "visible": true
  
  VOLUNTEER (MANDATORY FOR 2+ YEARS EXPERIENCE):
  - REQUIRED for 2+ years: Generate 1-2 volunteer experiences
  - Include organization, position, date, and impact summary
  - Focus on leadership and community impact
  - MUST set "visible": true
  - MUST include this section for users with 2+ years experience
  - DO NOT SKIP if experience >= 2 years
  
  PUBLICATIONS (10+ YEARS OR ACADEMIC):
  - Generate if 10+ years experience or academic role
  - Add name, publisher, date, and summary
  - Set "visible": true if generated
  
  CREATE A UNIQUE RESUME that best represents this candidate for their target role and market. Do not follow a one-size-fits-all template.
  
  FINAL CHECKLIST - ENSURE ALL THESE SECTIONS ARE PRESENT:
  ✓ Interests section with 2-3 items (ALL users) - MUST BE VISIBLE
  ✓ Hobbies section with 2-3 items (ALL users) - MUST BE VISIBLE
  ✓ Languages section with 2-3 items (ALL users) - MUST BE VISIBLE
  ✓ Projects section with 2-3 items (0-10 years experience) - MUST BE VISIBLE
  ✓ Awards section (2+ years experience) - MUST BE VISIBLE
  ✓ Volunteer section (2+ years experience) - MUST BE VISIBLE
  
  PLATFORM CONFLICT RESOLUTION:
  - If the platform says "Hide hobbies unless relevant" - IGNORE IT, show hobbies
  - If the platform says "Hide volunteer unless strong" - IGNORE IT, show volunteer for 2+ years
  - If the platform says "Hide awards unless notable" - IGNORE IT, show awards for 2+ years
  - Our requirements ALWAYS take precedence over platform defaults
  
  IF ANY OF THESE SECTIONS ARE MISSING OR SET TO visible:false, THE RESUME IS INCOMPLETE AND INCORRECT.`;
  },

  UserPromptStylingCVOld(options: PromptOptions): string {
    const {
      jobRole,
      country,
      yearsOfExperience,
      language,
      tone,
      atsStrength = "normal",
      onetDuties = [],
      onetTools = [],
      liveJobTrends = [],
      additionalInfo = {},
    } = options;

    // Get industry from additionalInfo if available
    const industry = additionalInfo.industry || detectIndustry(jobRole || "");

    // Get the optimal section order using the smart reorder function
    const sectionOrder = reorderResumeSections({
      jobRole: jobRole || "",
      yearsOfExperience: yearsOfExperience || 0,
      countryCode: getCountryCode(country || ""),
      industry,
    });

    // Convert section order to string format for prompt
    const sectionOrderString = sectionOrder.join(", ");

    // Define default sections that should always be included
    const defaultSections = [
      "Professional Summary",
      "Professional Experience",
      "Education",
      "Soft Skills & Behavioral Strengths",
      "Technical Skills & Tools",
      "Languages",
    ];

    // Define optional sections that should be included when relevant
    const optionalSections = [
      "Certifications & Licenses",
      "Objective",
      "Projects",
      "Publications",
      "Presentations",
      "Awards & Honors",
      "Volunteer Experience",
      "Hobbies & Interests",
      "References",
    ];

    // Get additional context from knowledge base if needed
    let roleGuidance = "";
    if (jobRole) {
      roleGuidance = getFromKnowledgeBase(
        "Expanded Job Role Smart Buckets (123 Domains).md",
        jobRole,
      );
    }

    let countryGuidance = "";
    if (country) {
      countryGuidance = getFromKnowledgeBase(
        "Expanded Country-Based Localization (105 Countries).md",
        country,
      );
    }

    // Process live job trends to separate out different categories
    const processedTrends = processTrends(liveJobTrends);

    // Set ATS target score range based on selected strength
    const atsTargetScoreRange = atsStrength === "normal" ? "85-92" : "93-100";

    // Build the prompt
    const prompt = `
You are an expert resume writer with extensive knowledge of global resume standards and ATS optimization.

Your task is to create a complete, ATS-optimized resume in valid JSON format, based on the following parameters:

JOB ROLE: ${jobRole || "Not specified"}
COUNTRY: ${country || "Not specified"}
EXPERIENCE LEVEL: ${yearsOfExperience ? `${yearsOfExperience} years` : "Not specified"}
LANGUAGE: ${language || "English"}
TONE: ${tone || "Professional"}
ATS OPTIMIZATION LEVEL: ${atsStrength === "normal" ? "Normal" : "Strong"}

RECOMMENDED SECTION ORDER: ${sectionOrderString}

DEFAULT SECTIONS (ALWAYS INCLUDE):
${defaultSections.join("\n")}

OPTIONAL SECTIONS (INCLUDE ONLY IF RELEVANT):
${optionalSections.join("\n")}

ATS TARGET SCORE: ${atsTargetScoreRange}%
The ATS score should be within the range of ${atsTargetScoreRange}%. If using 'strong' optimization level, incorporate more industry-specific keywords and ensure all critical skills from job description are included.

SECTION ORDER INSTRUCTIONS:
1. Start with the recommended section order: ${sectionOrderString}
2. Always include all default sections even if not in the recommended order
3. Add optional sections only if they are relevant for this specific job role and experience level
4. You may adjust the section order to:
   - Match country-specific standards for ${country || "the target country"}
   - Highlight role-specific strengths for ${jobRole || "the target role"}
   - Improve overall resume readability and flow

${onetDuties.length > 0 ? `O*NET DUTIES:\n${onetDuties.join("\n")}` : ""}

${onetTools.length > 0 ? `O*NET TOOLS:\n${onetTools.join("\n")}` : ""}

${processedTrends.requiredSkills ? `REQUIRED SKILLS:\n${processedTrends.requiredSkills}` : ""}

${processedTrends.emergingTechnologies ? `EMERGING TECHNOLOGIES:\n${processedTrends.emergingTechnologies}` : ""}

${processedTrends.valuableCertifications ? `VALUABLE CERTIFICATIONS:\n${processedTrends.valuableCertifications}` : ""}

${processedTrends.salaryRanges ? `SALARY RANGES:\n${processedTrends.salaryRanges}` : ""}

${processedTrends.industryTrends ? `INDUSTRY TRENDS:\n${processedTrends.industryTrends}` : ""}

${processedTrends.regionalDemands ? `REGIONAL DEMANDS:\n${processedTrends.regionalDemands}` : ""}

${processedTrends.softSkills ? `SOFT SKILLS:\n${processedTrends.softSkills}` : ""}

${processedTrends.otherTrends ? `ADDITIONAL MARKET INSIGHTS:\n${processedTrends.otherTrends}` : ""}

${additionalInfo.specificGuidance ? `ADDITIONAL GUIDANCE:\n${additionalInfo.specificGuidance}` : ""}

${roleGuidance ? `ROLE-SPECIFIC GUIDANCE:\n${roleGuidance}` : ""}

${countryGuidance ? `COUNTRY-SPECIFIC GUIDANCE:\n${countryGuidance}` : ""}

INSTRUCTIONS:
1. Create a complete resume in valid JSON format that follows the section order guidelines provided.
2. Always include all default sections, even if they weren't explicitly mentioned in the recommended section order.
3. Include optional sections only when they are relevant to this candidate's profile and target role.
4. Ensure all content is tailored to the job role, experience level, and follows country-specific standards.
5. Prioritize skills that appear in both O*NET tools and Required Skills sections.
6. Include relevant certifications from the Valuable Certifications section.
7. Reference salary expectations in alignment with the Salary Ranges information.
8. Use the specified tone and language throughout.
9. Structure all content for maximum readability and impact.
10. Incorporate emerging technologies in the skills section to show currency.
11. Include regional insights where appropriate in the professional summary.
12. REQUIRED: Generate work experiences based on years of experience (see PROFESSIONAL EXPERIENCE section)
13. REQUIRED: Include all O*NET tools in technical skills and all Perplexity soft skills
14. REQUIRED: Quantify all achievements with metrics (numbers, percentages, amounts)
15. CRITICAL: DO NOT include email field in basics - this will be handled by the user's profile
${
  atsStrength === "strong"
    ? `16. For Strong ATS optimization:
   - Include at least 80% of keywords from Required Skills
   - Format all job titles to match industry standard nomenclature
   - Include more specific metrics and achievements
   - Ensure all sections use ATS-friendly formatting
   - Emphasize relevant industry certifications`
    : ""
}

SECTION-SPECIFIC INSTRUCTIONS:

PROFESSIONAL SUMMARY:
- Integrate at least ${atsStrength === "strong" ? "7" : "5"} skills from O*NET TOOLS section and ${atsStrength === "strong" ? "3" : "2"} from REQUIRED SKILLS
- Reference industry-specific keywords from INDUSTRY TRENDS
- Mention career trajectory aligned with EXPERIENCE LEVEL
- Include ${atsStrength === "strong" ? "2-3" : "1-2"} accomplishments related to O*NET DUTIES
- Adapt tone to match specified TONE parameter
- For international positions, reference relevant REGIONAL DEMANDS

PROFESSIONAL EXPERIENCE:
 
- REQUIRED: Generate exactly \${targetExperienceCount} work experience entries
- For 0 years experience: Create 1 internship or relevant part-time/volunteer position
- For 1-2 years: Generate \${targetExperienceCount} entry-level positions
- For 3+ years: Generate \${targetExperienceCount} progressively responsible positions
- REQUIRED: Each experience entry must have exactly 4-5 achievement bullet points
- Each job entry should include at least ${atsStrength === "strong" ? "3" : "2"} duties directly related to O*NET DUTIES
- REQUIRED: Quantify achievements with specific metrics (percentages, numbers, dollar amounts)
- Incorporate at least ${atsStrength === "strong" ? "2" : "1"} emerging technology from EMERGING TECHNOLOGIES in recent roles
- Structure job entries to demonstrate clear career progression
- Include specific accomplishments that demonstrate skills from REQUIRED SKILLS
- Highlight leadership or collaborative aspects from SOFT SKILLS when appropriate
- For senior professionals (10+ years), focus on strategic impact and leadership achievements
- Achievement format: "Action verb + specific task/project + quantifiable result"

SKILLS SECTION (field name: "skills" - ITEMS ARRAY):
- REQUIRED: Structure as "items": [{"id": "skill-1", "visible": true, "name": "Technical Skills", "description": "Advanced", "level": 4, "keywords": [...]}, {"id": "skill-2", "visible": true, "name": "Soft Skills", "description": "Advanced", "level": 4, "keywords": [...]}]
- REQUIRED: First item must be "Technical Skills" with ALL tools from O*NET TOOLS section (minimum 8 skills)
- REQUIRED: Technical skills must include top ${atsStrength === "strong" ? "10" : "7"} items from EMERGING TECHNOLOGIES
- REQUIRED: Second item must be "Soft Skills" incorporating ALL items from SOFT SKILLS section (minimum 6 skills)
- Always use exactly "Technical Skills" and "Soft Skills" as the name values
- Organize keywords by relevance to the target role
- Always include skills relevant to INDUSTRY TRENDS

LANGUAGES SECTION:
- REQUIRED: Include all languages the candidate speaks
- REQUIRED: Set "level" based on proficiency:
  - Native/Mother tongue: level = 5, description = "Native"
  - Fluent/Excellent/Advanced: level = 4, description = "Fluent" or "Advanced"
  - Good/Professional/Intermediate: level = 3, description = "Professional" or "Good"
  - Basic/Elementary: level = 2, description = "Basic"
- REQUIRED: Always include "visible": true for each language
- First language should typically be the native language (level: 5)
- Add additional languages based on the country/region requirements

HOBBIES SECTION:
- REQUIRED: Include exactly 2-4 hobby items with icons
- REQUIRED: Each hobby must have:
  - "icon": Use appropriate icon names (e.g., "Basket-Ball", "News-paper", "Music-note", "Camera", "Book", "Game-controller", "Running", "Cooking", "Travel", "Art")
  - "name": The hobby name
  - "visible": true
  - "description": Optional, usually empty string
- Default hobbies if none provided: Basketball (icon: "Basket-Ball") and Reading (icon: "News-paper")
- Set columns: 1 for the hobbies section
- Choose hobbies that are professional yet personable

PROJECTS SECTION (for 3+ years experience):
- REQUIRED: Include 2-3 significant projects relevant to the target role
- Each project must have:
  * Name: Descriptive project title
  * Date range: When the project was completed
  * Description: Brief overview of the project
  * Summary: Detailed HTML description with technologies and outcomes
  * Keywords: Array of technologies used
  * URL: Link to project if available
- Focus on projects that demonstrate skills from EMERGING TECHNOLOGIES
- Quantify impact where possible

CERTIFICATIONS SECTION (for 3+ years experience):
- Include all relevant industry certifications
- Prioritize certifications mentioned in VALUABLE CERTIFICATIONS
- Each certification must include:
  * Name: Official certification name
  * Issuer: Certifying organization
  * Date: When obtained (YYYY-MM format)
  * URL: Verification link if available
- Include both current and in-progress certifications

AWARDS SECTION (for 6+ years experience):
- Include 2-3 professional awards or recognitions
- Each award must have:
  * Title: Award name
  * Awarder: Organization that gave the award
  * Date: When received (YYYY-MM-DD format)
  * Summary: Brief description of achievement
- Focus on awards that demonstrate excellence in the field

PUBLICATIONS SECTION (for 6+ years experience):
- Include relevant publications, articles, or papers
- Each publication must have:
  * Name: Publication title
  * Publisher: Where it was published
  * ReleaseDate: Publication date (YYYY-MM-DD format)
  * Summary: Brief description
  * URL: Link to publication if available
- Include both peer-reviewed and industry publications

VOLUNTEER SECTION (for 6+ years experience):
- Include 1-2 relevant volunteer experiences
- Each experience must have:
  * Organization: Name of organization
  * Position: Volunteer role
  * Date: Duration of service
  * Summary: HTML list of contributions and impact
- Focus on volunteer work that demonstrates leadership or relevant skills

INTERESTS SECTION (REQUIRED for all experience levels):
- MUST include 2-4 professional interests that show industry engagement and personal growth
- Each interest must have:
  * Name: Interest category (e.g., "Technology Innovation", "Professional Development")
  * Keywords: Array of specific interests within that category (NO empty strings)
- Choose interests that align with the job role and demonstrate continuous learning
- Examples of good interests: "Artificial Intelligence", "Leadership Development", "Industry Research"
`;

    return prompt;
  },

  /**
   * Build a targeted refinement prompt for a specific resume section
   * @param sectionName The name of the section (e.g., 'experience', 'summary')
   * @param sectionData The current JSON data of that section
   * @param suggestion The specific suggestion/instruction for improvement
   * @param targetRole The candidate's target role
   * @returns {string} The refinement prompt
   */
  getRefinementPrompt(
    sectionName: string,
    sectionData: any,
    suggestion: string,
    targetRole: string,
  ): string {
    return `You are a certified professional resume writer and ATS specialist.
    
    TASK: Refine the following resume section to address a specific improvement suggestion.
    
    TARGET ROLE: ${targetRole}
    SECTION: ${sectionName}
    CURRENT DATA: ${JSON.stringify(sectionData, null, 2)}
    
    IMPROVEMENT SUGGESTION: "${suggestion}"
    
    CRITICAL RULES:
    1. Focus HEAVILY on the improvement suggestion. 
    2. If the suggestion is "Quantify achievements", inject metrics (%, $, numbers) into every bullet point based on industry standards for ${targetRole}.
    3. If the suggestion is "Optimize Keywords", include high-impact keywords for ${targetRole}.
    4. Maintain the professional tone and same language as the current data.
    5. Return ONLY the refined JSON for this specific section.
    6. Ensure the result is valid JSON and matches the structure of the input data.
    7. Do NOT change the section "id" or "name".
    8. Use HTML formatting for summary fields (e.g. <ul><li> for experience bullets).
    
    OUTPUT: Valid JSON only.`;
  },

  /**
   * Helper functions for validation of resume sections
   */

  /**
   * Validates if a section exists in the content
   * @param {string} content The resume content to check
   * @param {string} sectionTitle The section title to look for
   * @returns {boolean} True if section exists, false otherwise
   */
  validateSection(content: string, sectionTitle: string): boolean {
    // Create various forms of section headings to check
    const sectionVariations = [
      new RegExp(`\\*\\*${sectionTitle}\\*\\*`, "i"), // **Section**
      new RegExp(`\\*\\*${sectionTitle}:\\*\\*`, "i"), // **Section:**
      new RegExp(`##\\s*${sectionTitle}`, "i"), // ## Section
      new RegExp(`###\\s*${sectionTitle}`, "i"), // ### Section
      new RegExp(`\\b${sectionTitle}\\b`, "i"), // Plain "Section"
    ];

    return sectionVariations.some((regex) => regex.test(content));
  },

  /**
   * Gets a list of missing sections in the resume content
   * @param {string} content The resume content to check
   * @param {string} jobRole The job role
   * @param {string} experience The experience level
   * @returns {string[]} Array of missing section titles
   */
  getMissingSections(content: string, jobRole: string, experience: string): string[] {
    // Remove score lines for processing
    const contentWithoutScores = content
      .replace(/ats score:\s*\d+/i, "")
      .replace(/job fit score:\s*\d+/i, "")
      .trim();

    // Standard required sections for all resumes
    const requiredSectionTitles = [
      "Professional Summary",
      "Professional Experience",
      "Education",
      "Soft Skills & Behavioral Strengths",
      "Technical Skills & Tools",
      "Certifications",
      "Languages",
    ];

    // Check which sections are missing
    const missingSections: string[] = [];

    for (const section of requiredSectionTitles) {
      if (section && !PromptBuilder.validateSection(contentWithoutScores, section)) {
        missingSections.push(section);
      }
    }

    return missingSections;
  },
};

/**
 * Process trend data into structured categories
 * @param {string[]} trends
 * @returns {object} Structured trend data by category
 */
function processTrends(trends: string[]): Record<string, string> {
  // Initialize categories
  const categories: Record<string, string> = {
    requiredSkills: "",
    emergingTechnologies: "",
    valuableCertifications: "",
    salaryRanges: "",
    industryTrends: "",
    regionalDemands: "",
    softSkills: "",
    otherTrends: "",
  };

  // Check if trends is empty
  if (!trends || trends.length === 0) {
    return categories;
  }

  // Initialize current category
  let currentCategory = "otherTrends";

  // Process each line
  for (const line of trends) {
    const cleanLine = line.trim();

    // Skip empty lines
    if (!cleanLine) continue;

    // Check for category headers
    if (cleanLine.toUpperCase().includes("REQUIRED SKILLS")) {
      currentCategory = "requiredSkills";
      continue;
    } else if (cleanLine.toUpperCase().includes("EMERGING TECHNOLOGIES")) {
      currentCategory = "emergingTechnologies";
      continue;
    } else if (cleanLine.toUpperCase().includes("VALUABLE CERTIFICATIONS")) {
      currentCategory = "valuableCertifications";
      continue;
    } else if (cleanLine.toUpperCase().includes("SALARY RANGES")) {
      currentCategory = "salaryRanges";
      continue;
    } else if (cleanLine.toUpperCase().includes("INDUSTRY TRENDS")) {
      currentCategory = "industryTrends";
      continue;
    } else if (cleanLine.toUpperCase().includes("REGIONAL DEMANDS")) {
      currentCategory = "regionalDemands";
      continue;
    } else if (cleanLine.toUpperCase().includes("SOFT SKILLS")) {
      currentCategory = "softSkills";
      continue;
    }

    // Add line to current category
    categories[currentCategory] = (categories[currentCategory] || "") + cleanLine + "\n";
  }

  // Clean up each category (trim trailing newlines)
  for (const key of Object.keys(categories)) {
    categories[key] = categories[key].trim();
  }

  return categories;
}

/**
 * Extracts a country code from a country name
 * @param {string} countryName Full country name
 * @returns {string} ISO country code or empty string if not found
 */
export function getCountryCode(countryName: string): string {
  if (!countryName) return "";

  // Map of common country names to their ISO codes
  const countryMap: Record<string, string> = {
    "united states": "US",
    usa: "US",
    "united states of america": "US",
    canada: "CA",
    "united kingdom": "UK",
    uk: "UK",
    "great britain": "UK",
    australia: "AU",
    germany: "DE",
    france: "FR",
    japan: "JP",
    china: "CN",
    india: "IN",
    brazil: "BR",
    mexico: "MX",
    spain: "ES",
    italy: "IT",
    russia: "RU",
    "south korea": "KR",
    netherlands: "NL",
    sweden: "SE",
    switzerland: "CH",
    singapore: "SG",
    "united arab emirates": "AE",
    uae: "AE",
    "saudi arabia": "SA",
  };

  const normalizedCountry = countryName.toLowerCase().trim();
  return countryMap[normalizedCountry] || "";
}

/**
 * Attempts to detect the industry based on job role
 * @param {string} jobRole Job role/title
 * @returns {string} Detected industry or empty string
 */
export function detectIndustry(jobRole: string): string {
  if (!jobRole) return "";

  const jobRoleLower = jobRole.toLowerCase();

  // Simple keyword-based industry detection
  if (
    /software|developer|programmer|code|web|app|frontend|backend|fullstack|devops/.test(
      jobRoleLower,
    )
  ) {
    return "Tech";
  }

  if (/finance|accountant|financial|analyst|banking|investment|cfo|controller/.test(jobRoleLower)) {
    return "Finance";
  }

  if (
    /nurse|doctor|physician|healthcare|medical|clinical|patient|hospital|pharma/.test(jobRoleLower)
  ) {
    return "Healthcare";
  }

  if (/designer|creative|artist|ux|ui|graphic|visual|photography|video/.test(jobRoleLower)) {
    return "Creative";
  }

  return "";
}
